import { Video360, type VideoChapter } from './Video360';
import { useState } from 'react';
import { Panorama360 } from './Panorama360';
import { photoUrl, type ListingPanorama } from './ListingCard';

export const panoramaUrl = (id: string) => `/api/v1/discover/photos/${id}`;
export const videoUrl = (id: string) => `/api/v1/discover/videos/${id}`;

/**
 * Everything a renter can look at, in one place.
 *
 * Tabs rather than a long stack, because these are three ways of answering the
 * SAME question — what is this place like — and a renter wants to switch
 * between them, not scroll past two to reach the third. The tab strip appears
 * only when there is more than one kind of media, so a listing with five
 * photographs and nothing else looks exactly as it did before this existed.
 *
 * Photos stay first and stay the default. The 360 tour is the better answer
 * but the slower one to load, and a gallery that starts by pulling three
 * megabytes of panorama on a mobile bundle would be a worse page for the
 * majority who just want to see the kitchen.
 *
 * WHERE THE BYTES COME FROM IS A PROP. The rental endpoints are the default,
 * so every existing caller is untouched; a sale listing passes its own three
 * builders. This is the whole of what the component needed in order to serve
 * both — everything else here is about arrangement, and arranging a sale
 * gallery differently from a rental one would be a difference with no reason
 * behind it.
 */

type Tab = 'photos' | 'tour' | 'video';

export interface MediaUrls {
  photo: (id: string) => string;
  panorama: (id: string) => string;
  video: (id: string) => string;
}

/** Where a rental listing's media lives. The default, so callers may omit it. */
export const RENTAL_MEDIA_URLS: MediaUrls = {
  photo: photoUrl, panorama: panoramaUrl, video: videoUrl,
};

export function ListingMedia({
  title, photoIds, panoramas, videoId, videoProjection, videoChapters, urls = RENTAL_MEDIA_URLS,
}: {
  title: string;
  photoIds: string[];
  panoramas: ListingPanorama[];
  videoId: string | null;
  /** 'equirect' plays in the 360 viewer (migration 165); anything else plays flat. */
  videoProjection?: string | null;
  /** Where each room begins in a 360 video. */
  videoChapters?: VideoChapter[] | null;
  urls?: MediaUrls;
}) {
  const is360 = videoProjection === 'equirect';
  const [tab, setTab] = useState<Tab>('photos');
  const [active, setActive] = useState(0);
  const [room, setRoom] = useState(0);

  const tabs: { id: Tab; label: string; count?: number }[] = [
    ...(photoIds.length ? [{ id: 'photos' as const, label: 'Photos', count: photoIds.length }] : []),
    ...(panoramas.length ? [{ id: 'tour' as const, label: '360° tour', count: panoramas.length }] : []),
    ...(videoId ? [{ id: 'video' as const, label: is360 ? '360° video' : 'Video' }] : []),
  ];

  // A listing with no media at all keeps the placeholder it always had.
  if (tabs.length === 0) return <div className="dsc-gallery"><div className="dsc-gallery-none" aria-hidden>⌂</div></div>;

  const current = tabs.some((t) => t.id === tab) ? tab : tabs[0]!.id;
  const pano = panoramas[Math.min(room, panoramas.length - 1)];

  return (
    <div className="dsc-media">
      {tabs.length > 1 && (
        <div className="dsc-media-tabs" role="tablist" aria-label="Ways to view this home">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab"
              id={`media-tab-${t.id}`} aria-controls={`media-panel-${t.id}`}
              aria-selected={current === t.id}
              className={`dsc-media-tab${current === t.id ? ' on' : ''}`}
              onClick={() => setTab(t.id)}>
              {t.label}{t.count !== undefined && <span className="dsc-media-n">{t.count}</span>}
            </button>
          ))}
        </div>
      )}

      {current === 'photos' && (
        <div className="dsc-gallery" role="tabpanel" id="media-panel-photos" aria-labelledby="media-tab-photos">
          <img className="dsc-gallery-main" src={urls.photo(photoIds[active] ?? photoIds[0]!)}
            alt={`${title}, photo ${active + 1} of ${photoIds.length}`} />
          {photoIds.length > 1 && (
            <div className="dsc-thumbs">
              {photoIds.map((p, i) => (
                <button key={p} type="button" className={`dsc-thumb${i === active ? ' on' : ''}`}
                  aria-label={`Photo ${i + 1}`} aria-current={i === active}
                  onClick={() => setActive(i)}>
                  <img src={urls.photo(p)} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {current === 'tour' && pano && (
        <div role="tabpanel" id="media-panel-tour" aria-labelledby="media-tab-tour">
          <Panorama360
            // Keyed by id so switching rooms rebuilds the viewer rather than
            // leaving the previous room's texture up while the next decodes.
            key={pano.id}
            src={urls.panorama(pano.id)}
            alt={`${pano.label ?? pano.section} of ${title}`}
            className="dsc-pano"
          />
          {panoramas.length > 1 && (
            <div className="dsc-rooms" role="tablist" aria-label="Rooms in this tour">
              {panoramas.map((p, i) => (
                <button key={p.id} type="button" role="tab" aria-selected={i === room}
                  className={`dsc-room${i === room ? ' on' : ''}`}
                  onClick={() => setRoom(i)}>
                  {p.label ?? p.section}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {current === 'video' && videoId && is360 && (
        <div className="dsc-video" role="tabpanel" id="media-panel-video" aria-labelledby="media-tab-video">
          <Video360 src={urls.video(videoId)} chapters={videoChapters ?? []} title={title} />
        </div>
      )}

      {current === 'video' && videoId && !is360 && (
        <div className="dsc-video" role="tabpanel" id="media-panel-video" aria-labelledby="media-tab-video">
          {/*
            * `preload="metadata"` on purpose: enough to show the duration and
            * the scrub bar, nothing more. `preload="auto"` would start pulling
            * tens of megabytes from every visitor who scrolls past, most of
            * whom never press play, and they are paying for the bundle.
            */}
          <video
            controls
            preload="metadata"
            playsInline
            poster={photoIds[0] ? urls.photo(photoIds[0]) : undefined}
            src={urls.video(videoId)}
          >
            Your browser cannot play this video.
          </video>
        </div>
      )}
    </div>
  );
}
